/**
 * Key editor navigation (2026-09-14, Sarah): walk the training set from
 * inside the editor instead of going back to the list, remembering the row,
 * and pressing Edit key. Pure so it can be tested without the page.
 *
 * Order is the list's order (the server sorts by sequence, then added time).
 * Retired items are skipped — the list hides them by default, so a Senior
 * keying "the next one" never means a retired item.
 */
export interface KeyNavItem {
  id: string;
  status: "draft" | "ready" | "retired";
}

export interface KeyNav {
  /** 1-based position of the current item among the walkable ones; null when the current item is retired or unknown. */
  position: number | null;
  /** Walkable (non-retired) items. */
  total: number;
  prevId: string | null;
  nextId: string | null;
  /** Next draft item after the current one, wrapping to the start; never the current item. */
  nextDraftId: string | null;
}

export function keyNavFor(items: readonly KeyNavItem[], currentId: string): KeyNav {
  const walk = items.filter((i) => i.status !== "retired");
  const idx = walk.findIndex((i) => i.id === currentId);
  const total = walk.length;
  if (idx === -1) {
    // Current item is retired (or not in the list yet): offer the first draft as a way onward.
    const firstDraft = walk.find((i) => i.status === "draft") ?? null;
    return { position: null, total, prevId: null, nextId: null, nextDraftId: firstDraft?.id ?? null };
  }
  let nextDraftId: string | null = null;
  for (let step = 1; step < walk.length; step++) {
    const cand = walk[(idx + step) % walk.length];
    if (cand.status === "draft") { nextDraftId = cand.id; break; }
  }
  return {
    position: idx + 1,
    total,
    prevId: idx > 0 ? walk[idx - 1].id : null,
    nextId: idx < walk.length - 1 ? walk[idx + 1].id : null,
    nextDraftId,
  };
}

/** True when a keydown should be treated as an editor shortcut (not typing in a field). */
export function isShortcutTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node || typeof node.closest !== "function") return true;
  return !node.closest("input, textarea, select, [contenteditable=''], [contenteditable='true']");
}
